import React, {useState} from 'react';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import {Autocomplete} from '@mui/material';
import PropTypes from 'prop-types';
import {validateEmail} from '../utility/validationUtil';

const EMAILS_LIMIT = 5;

const EmailChipsInput = (
    {
        values,
        onChange,
        onBlur,
        label,
        disabled,
        hideHelperText = false,
        size,
    }) => {
    const [inputValue, setInputValue] = useState('');
    // Walkthrough findings 15 and 24: an address that cannot be added stays in the box, with the reason under it.
    const [error, setError] = useState('');

    const handleChange = (e, newValues) => {
        const typed = newValues.find(x => typeof x !== 'object');

        if (newValues.length > EMAILS_LIMIT) {
            setError('You can enter up to five email addresses.');
            setInputValue(typed ?? '');

            return;
        }

        if (typed !== undefined && !validateEmail(typed.trim()).isValid) {
            setError('Please enter a valid email address.');
            setInputValue(typed);

            return;
        }

        setError('');

        const validEmails = newValues
            .map(x => {
                if (typeof x === 'object') {
                    return x;
                }

                return validateEmail(x.trim()).isValid ? {email: x.trim()} : null;
            })
            .filter(x => x != null);

        onChange(validEmails);
    };

    const handleBlur = (e) => {
        if (values.length >= EMAILS_LIMIT) {
            setInputValue('');

            return;
        }

        const email = (e.target.value ?? inputValue ?? '').trim();

        if (!email) {
            return;
        }

        if (!validateEmail(email).isValid) {
            setError('Please enter a valid email address.');

            return;
        }

        onBlur(email);
        setInputValue('');
    };

    return (
        <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={values}
            limitTags={5}
            onChange={handleChange}
            onBlur={handleBlur}
            inputValue={inputValue}
            disabled={disabled}
            onInputChange={(event, newInputValue, reason) => {
                // Pressing Enter on an address that is refused would otherwise empty the box.
                if (reason === 'reset' && error) {
                    return;
                }
                setInputValue(newInputValue);
                if (reason === 'input') {
                    setError('');
                }
            }}
            renderTags={(value, getTagProps) =>
                value.map((value, index) => (
                    <Chip
                        key={index}
                        label={value.email}
                        {...getTagProps({index})}
                    />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    size={size}
                    label={label}
                    disabled={disabled}
                    type="email"
                    error={!!error}
                    helperText={error || (hideHelperText ? undefined : `Enter email addresses, pressing Enter after each one (max ${EMAILS_LIMIT} allowed).`)}
                />
            )}
            sx={{
                '&.MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input': {
                    minWidth: '40%',
                }
            }}
        />
    );
};

EmailChipsInput.defaultProps = {
    label: 'Enter emails',
    disabled: false,
};

EmailChipsInput.propTypes = {
    values: PropTypes.array,
    label: PropTypes.string,
    hideHelperText: PropTypes.bool,
    size: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    disabled: PropTypes.bool,
};

export default EmailChipsInput;
